<template>
    <div class="content">
        <div class="block-rounded block">
            <div class="block-header block-header-default d-flex justify-content-between align-items-center">
                <h3 class="block-title m-0">
                    {{ $t('title_add_new_transaction') }}
                </h3>
            </div>

            <div class="block-content block-content-full">
                <form @submit.prevent="handleSubmit">
                    <!-- From and To Inputs -->
                    <div class="row mb-3">
                        <div class="col">
                            <label class="form-label" for="from">{{ $t('input_from') }}
                                <span class="text-danger">*</span>
                                <span v-if="selectedDebtorAccount" :class="{
                                    'fs-xs fw-semibold d-inline-block py-1 px-3 ms-2 rounded-pill': true,
                                    'bg-success-light text-success': selectedDebtorAccount.balance > 0,
                                    'bg-danger-light text-danger': selectedDebtorAccount.balance < 0,
                                    'bg-warning-light text-warning': selectedDebtorAccount.balance === 0
                                }">
                                    Balance: {{ selectedDebtorAccount.balance }}
                                </span>
                            </label>
                            <Select v-model="form.debtor_account_id_input"
                                :placeholder="$t('placeholder.select_debtor')" :options="accounts" optionLabel="title"
                                optionValue="_id" filter required :maxSelectedLabels="3" class="w-full h-9"
                                :selectionLimit="null">
                                <template #option="slotProps">
                                    <span class="badge p-2 flex items-center gap-2"
                                        :class="['bg-' + slotProps.option.type.color]">
                                        {{ slotProps.option.title }} - {{ slotProps.option.type.title_ar }}
                                    </span>
                                </template>
                            </Select>
                        </div>

                        <div class="col">
                            <label class="form-label" for="to">{{ $t('input_to') }}<span class="text-danger">*</span>
                                <span v-if="selectedCreditorAccount" :class="{
                                    'fs-xs fw-semibold d-inline-block py-1 px-3 ms-2 rounded-pill': true,
                                    'bg-success-light text-success': selectedCreditorAccount.balance > 0,
                                    'bg-danger-light text-danger': selectedCreditorAccount.balance < 0,
                                    'bg-warning-light text-warning': selectedCreditorAccount.balance === 0
                                }">
                                    Balance: {{ selectedCreditorAccount.balance }}
                                </span>
                            </label>
                            <Select v-model="form.creditor_account_id_input"
                                :placeholder="$t('placeholder.select_creditor')" :options="accounts" optionLabel="title"
                                optionValue="_id" filter required :maxSelectedLabels="3" class="w-full text-sm h-9"
                                :selectionLimit="null">
                                <template #option="slotProps">
                                    <span class="badge p-2 flex items-center gap-2"
                                        :class="['bg-' + slotProps.option.type.color]">
                                        {{ slotProps.option.title }} - {{ slotProps.option.type.title_ar }}
                                    </span>
                                </template>
                            </Select>
                        </div>
                    </div>
                    <!-- Value and Notes -->
                    <div class="row mb-3">
                        <div class="col-md-6">
                            <label class="form-label" for="value">{{ $t('input_value') }}<span
                                    class="text-danger">*</span></label>

                            <InputGroup>
                                <InputGroupAddon>EGP</InputGroupAddon>
                                <InputNumber v-model="form.transaction_value_input" class="w-full text-sm" />
                                <InputGroupAddon>.00</InputGroupAddon>
                            </InputGroup>


                        </div>

                        <div class="col-md-6">
                            <label class="form-label" for="notes">{{ $t('input_notes') }}</label>
                            <textarea v-model="form.transaction_notes_input" class="form-control" rows="3"></textarea>
                        </div>
                    </div>
                    <TransactionInfo v-if="selectedDebtorAccount && selectedCreditorAccount"
                        :debtorType="selectedDebtorAccount.type" :creditorType="selectedCreditorAccount.type" />
                    <h2 class="content-heading border-bottom mb-4 pb-2"></h2>
                    <!-- Submit Button -->
                    <div class="d-flex justify-content-end mb-4">
                        <button type="submit" class="btn btn-sm btn-primary">
                            <i v-if="!loading" class="fa fa-plus me-1"></i> <!-- Show Plus Icon when not loading -->
                            <div v-if="loading" class="spinner-border spinner-border-sm me-1" role="status">
                                <span class="sr-only">Loading...</span>
                            </div>
                            {{ $t('button_submit') }}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import swal from '@/utils/swal'
import TransactionInfo from './components/TransactionInfo.vue'

const form = ref({
    debtor_account_id_input: '',
    creditor_account_id_input: '',
    transaction_value_input: '',
    transaction_notes_input: '',
})

let loading = ref(false)

// Example accounts structure - replace with prop or API call
let accounts = ref([])

const selectedDebtorAccount = computed(() =>
    accounts.value.find(acc => acc._id === form.value.debtor_account_id_input)
)

const selectedCreditorAccount = computed(() =>
    accounts.value.find(acc => acc._id === form.value.creditor_account_id_input)
)


const handleSubmit = async () => {
    // Custom validation
    if (!form.value.debtor_account_id_input) {
        showError("Please select a 'From' account.")
        return
    }

    if (!form.value.creditor_account_id_input) {
        showError("Please select a 'To' account.")
        return
    }

    if (form.value.debtor_account_id_input === form.value.creditor_account_id_input) {
        showError("Debtor and Creditor accounts cannot be the same.")
        return
    }

    if (!form.value.transaction_value_input) {
        showError("Please enter a transaction value.")
        return
    }

    loading.value = true

        axios.post('/transactions/post', form.value, {
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
            },
        })
        .then(response => {
            console.log(response);

            if (response.data.status === 'success') {
                swal.success(response.data.message);
                fetchAccounts(); // refresh balances
            } else {
                showError(response.data.message || 'Unexpected error occurred.');
            }
        })
        .catch(error => {
            showError(error.response?.data?.message || 'An error occurred while processing the transaction.');
        })
        .finally(() => {
            form.value = {
                debtor_account_id_input: '',
                creditor_account_id_input: '',
                transaction_value_input: '',
                transaction_notes_input: '',
            };
            loading.value = false;
        });

}

function fetchAccounts() {
    axios.get('/accounts/get-active-accounts')
        .then(response => {
            accounts.value = response.data;
        })
        .catch(error => {
            console.error('Error fetching users:', error);
        });
};

function showError(message) {
    Swal.fire({
        icon: 'error',
        title: 'Error',
        text: message,
    });
}

onMounted(() => {
    // Fetch accounts when the component is mounted
    fetchAccounts()
    // Re-initialize Bootstrap Select if needed
    setTimeout(() => {
        if (window.$ && window.$('.selectpicker').selectpicker) {
            window.$('.selectpicker').selectpicker('refresh')
        }
    }, 100)
})
</script>